import { Controller } from "@hotwired/stimulus"
import { track as mixpanelTrack } from "../lib/mixpanel"

// Phase 2.4 Tier 2 — PO Modifications tab controller. Handles the
// create-modification flow, the line-delta grid (add/modify/remove),
// and the state transitions (submit / approve / reject / withdraw).
// Follows the same conventions as `purchase_order_controller.js`:
// DOM patches for fast in-place updates, `_flashAndReload` for state
// transitions that change the parent PO header, modal backdrop dismiss.
export default class extends Controller {
  static targets = [
    "modList", "modCard", "modBody", "chevron", "lineBody",
    "createModal", "createReason", "createEffective",
    "addLineModal", "addLineAction", "addLineTarget", "addLineTargetWrap",
    "addLineDescWrap", "addLineDesc", "addLineNumbers",
    "addLineQty", "addLineUnitCost",
    "rejectModal", "rejectReason"
  ]

  static values = {
    projectId: Number,
    poId: Number,
    poLines: Array,
    canApprove: Boolean
  }

  connect() {
    this._csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
    this._activeModId = null
    this._rejectingModId = null
  }

  // ── Create modification ────────────────────────────────────────────
  openCreateModal() { this._show(this.createModalTarget) }
  hideCreateModal() { this._hide(this.createModalTarget) }

  async confirmCreate() {
    const body = {
      purchase_order_modification: {
        reason: this.createReasonTarget.value.trim(),
        effective_date: this.createEffectiveTarget.value || null
      }
    }
    const resp = await this._ajax("POST", this._modsUrl(), body)
    if (!resp.ok) {
      this._showToast(resp.body?.errors?.join(", ") || resp.body?.error || "Could not create modification", "error")
      return
    }
    this._track("Purchase Order Modification Created", { modification_id: resp.body.id })
    // Full reload — the whole tab needs to re-render with the new card.
    this._flashAndReload("Modification drafted")
  }

  // ── Expand/collapse cards ──────────────────────────────────────────
  toggleMod(event) {
    const card = event.currentTarget.closest("[data-mod-id]")
    if (!card) return
    const body = card.querySelector('[data-po-modifications-target="modBody"]')
    const chevron = card.querySelector('[data-po-modifications-target="chevron"]')
    if (!body) return
    body.classList.toggle("hidden")
    if (chevron) chevron.classList.toggle("rotate-180")
  }

  // ── Add line-delta row ─────────────────────────────────────────────
  openAddLineModal(event) {
    this._activeModId = event.currentTarget.dataset.modId
    this.addLineActionTarget.value = "modify"
    this.actionChanged()
    this._show(this.addLineModalTarget)
  }

  hideAddLineModal() {
    this._activeModId = null
    this._hide(this.addLineModalTarget)
  }

  // Toggle which form fields show based on the chosen action —
  //   add    → need description + qty + unit cost; hide target picker
  //   modify → need target + (optionally any of qty Δ, unit cost, desc)
  //   remove → need target only; hide description + numbers
  actionChanged() {
    const action = this.addLineActionTarget.value
    if (action === "add") {
      this._hide(this.addLineTargetWrapTarget)
      this._show(this.addLineDescWrapTarget)
      this._show(this.addLineNumbersTarget)
    } else if (action === "remove") {
      this._show(this.addLineTargetWrapTarget)
      this._hide(this.addLineDescWrapTarget)
      this._hide(this.addLineNumbersTarget)
    } else {
      this._show(this.addLineTargetWrapTarget)
      this._show(this.addLineDescWrapTarget)
      this._show(this.addLineNumbersTarget)
    }
  }

  async confirmAddLine() {
    if (!this._activeModId) return

    const action = this.addLineActionTarget.value
    const body = { purchase_order_modification_line_item: { action } }

    if (action === "add") {
      body.purchase_order_modification_line_item.description_new = this.addLineDescTarget.value.trim()
      body.purchase_order_modification_line_item.qty_delta = parseFloat(this.addLineQtyTarget.value) || 0
      body.purchase_order_modification_line_item.unit_cost_new = parseFloat(this.addLineUnitCostTarget.value) || 0
    } else if (action === "modify") {
      const target = this.addLineTargetTarget.value
      if (!target) { this._showToast("Pick a target line", "error"); return }
      body.purchase_order_modification_line_item.purchase_order_line_item_id = parseInt(target, 10)
      body.purchase_order_modification_line_item.qty_delta = parseFloat(this.addLineQtyTarget.value) || 0
      const unitCostRaw = this.addLineUnitCostTarget.value
      if (unitCostRaw !== "") {
        body.purchase_order_modification_line_item.unit_cost_new = parseFloat(unitCostRaw)
      }
      const desc = this.addLineDescTarget.value.trim()
      if (desc) body.purchase_order_modification_line_item.description_new = desc
    } else if (action === "remove") {
      const target = this.addLineTargetTarget.value
      if (!target) { this._showToast("Pick a target line", "error"); return }
      body.purchase_order_modification_line_item.purchase_order_line_item_id = parseInt(target, 10)
    }

    const resp = await this._ajax("POST", `${this._modsUrl()}/${this._activeModId}/line_items`, body)
    if (!resp.ok) {
      this._showToast(resp.body?.errors?.join(", ") || resp.body?.error || "Could not add line change", "error")
      return
    }
    this.hideAddLineModal()
    this._flashAndReload("Line change added")
  }

  async deleteModLine(event) {
    const modId = event.currentTarget.dataset.modId
    const lineId = event.currentTarget.dataset.lineId
    if (!modId || !lineId) return

    const resp = await this._ajax("DELETE", `${this._modsUrl()}/${modId}/line_items/${lineId}`)
    if (!resp.ok) {
      this._showToast(resp.body?.error || "Could not remove", "error")
      return
    }
    event.currentTarget.closest("tr")?.remove()
  }

  async deleteMod(event) {
    const modId = event.currentTarget.dataset.modId
    const resp = await this._ajax("DELETE", `${this._modsUrl()}/${modId}`)
    if (!resp.ok) {
      this._showToast(resp.body?.error || "Could not delete", "error")
      return
    }
    this._flashAndReload("Draft modification discarded")
  }

  // ── State transitions ─────────────────────────────────────────────
  async submitMod(event) {
    const modId = event.currentTarget.dataset.modId
    const resp = await this._ajax("POST", `${this._modsUrl()}/${modId}/submit`)
    if (!resp.ok) { this._showToast(resp.body?.error || "Submit failed", "error"); return }
    this._track("Purchase Order Modification Submitted", { modification_id: parseInt(modId, 10) })
    this._flashAndReload("Modification submitted for approval")
  }

  async approveMod(event) {
    const modId = event.currentTarget.dataset.modId
    const resp = await this._ajax("POST", `${this._modsUrl()}/${modId}/approve`)
    if (!resp.ok) { this._showToast(resp.body?.error || "Approve failed", "error"); return }
    this._track("Purchase Order Modification Approved", { modification_id: parseInt(modId, 10) })
    this._flashAndReload("Modification approved and applied to the PO")
  }

  rejectMod(event) {
    this._rejectingModId = event.currentTarget.dataset.modId
    this._show(this.rejectModalTarget)
  }

  hideRejectModal() {
    this._rejectingModId = null
    this._hide(this.rejectModalTarget)
  }

  async confirmReject() {
    if (!this._rejectingModId) return
    const reason = this.rejectReasonTarget.value.trim()
    this._hide(this.rejectModalTarget)
    const resp = await this._ajax("POST", `${this._modsUrl()}/${this._rejectingModId}/reject`, { reason })
    if (!resp.ok) { this._showToast(resp.body?.error || "Reject failed", "error"); return }
    this._track("Purchase Order Modification Rejected", { modification_id: parseInt(this._rejectingModId, 10) })
    this._flashAndReload("Modification rejected")
  }

  // ── plumbing ──────────────────────────────────────────────────────
  stopPropagation(event) { event.stopPropagation() }

  _show(el) { el?.classList.remove("hidden") }
  _hide(el) { el?.classList.add("hidden") }

  _modsUrl() {
    return `/projects/${this.projectIdValue}/purchase_orders/${this.poIdValue}/modifications`
  }

  _flashAndReload(message, type = "success") {
    sessionStorage.setItem("purchase_order_flash", JSON.stringify({ message, type }))
    window.location.reload()
  }

  _showToast(message, type = "success") {
    const cssType = (type === "error" || type === "alert") ? "alert" : "notice"
    const el = document.createElement("div")
    el.className = `flash-toast-${cssType}`
    el.setAttribute("data-controller", "flash")
    el.setAttribute("data-flash-dismiss-after-value", "5000")
    el.setAttribute("role", "alert")
    const msg = document.createElement("p")
    msg.className = "flex-1 text-sm"
    msg.textContent = message
    el.appendChild(msg)
    document.body.prepend(el)
  }

  async _ajax(method, url, payload) {
    const options = {
      method,
      headers: { "X-CSRF-Token": this._csrfToken, "Accept": "application/json" }
    }
    if (payload !== undefined && method !== "GET") {
      options.headers["Content-Type"] = "application/json"
      options.body = JSON.stringify(payload)
    }
    try {
      const resp = await fetch(url, options)
      const ct = resp.headers.get("Content-Type") || ""
      const body = ct.includes("application/json") ? await resp.json().catch(() => null) : null
      return { ok: resp.ok, status: resp.status, body }
    } catch (e) {
      return { ok: false, status: 0, body: { error: e.message } }
    }
  }

  _track(name, props = {}) {
    try {
      mixpanelTrack(name, { project_id: this.projectIdValue, purchase_order_id: this.poIdValue, ...props })
    } catch { /* ignore */ }
  }
}
